import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Table,
  Badge,
  NativeSelect,
  Button,
  Progress,
  Spinner,
  useDisclosure,
  SimpleGrid,
  Input,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { Thead, Tbody, Tr, Th, Td, Switch} from '@/components/ui/chakra-v2-compat';
import { toaster } from '@/components/ui/toaster';
import { Tooltip } from '@/components/ui/tooltip';
import { adminAPI } from '../services/api';

const PLAN_TIERS = ['GRATIS', 'ESTANDAR', 'INTERMEDIO', 'AVANZADO', 'COFRADIA'];

const PLAN_LABELS = {
  GRATIS: 'Gratis',
  ESTANDAR: 'Estándar',
  INTERMEDIO: 'Intermedio',
  AVANZADO: 'Avanzado',
  COFRADIA: 'Cofradía',
};

const FEATURE_LABELS = {
  perfilPublico: 'Perfil público',
  comunidad: 'Comunidad',
  tuWeb: 'Tu Web',
  dominioPropio: 'Dominio propio',
  calendario: 'Calendario',
  eventos: 'Eventos',
  contactos: 'Contactos',
  mensajeria: 'Mensajería',
  acuerdos: 'Acuerdos',
  archivoMusical: 'Archivo musical',
  musicos: 'Mis músicos',
  formaciones: 'Formaciones',
  actuaciones: 'Actuaciones',
  ensayos: 'Ensayos',
  documentos: 'Documentos',
  estadisticas: 'Estadísticas',
  objetivos: 'Objetivos',
  encuestas: 'Encuestas',
  permisos: 'Permisos',
};

const eur = (n) => `${Number(n || 0).toFixed(2)} €`;

const storageCell = (budget) => {
  if (!budget) return '—';
  const usedMb = Number(budget.storageBytes || 0) / 1024 / 1024;
  const used =
    usedMb >= 1024
      ? `${(usedMb / 1024).toFixed(1)} GB`
      : `${usedMb.toFixed(1)} MB`;
  const q = budget.storageQuotaMb;
  if (q == null) return `${used} / ∞`;
  const cap =
    q >= 1024
      ? `${(q / 1024).toFixed(q % 1024 === 0 ? 0 : 1)} GB`
      : `${q} MB`;
  return `${used} / ${cap}`;
};

const storageOverQuota = (budget) => {
  const q = budget?.storageQuotaMb;
  if (q == null) return false;
  return Number(budget.storageBytes || 0) >= q * 1024 * 1024;
};
const usd = (n) => `${Number(n || 0).toFixed(4)} $`;

const AdminPlanesPanel = () => {
  
  const [rows, setRows] = useState([]);
  const [featureKeys, setFeatureKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [overridesDraft, setOverridesDraft] = useState({});
  const [budgetDraft, setBudgetDraft] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const { open: isOpen, onOpen, onClose } = useDisclosure();

  const [obrasRow, setObrasRow] = useState(null);
  const [obrasData, setObrasData] = useState(null);
  const [obrasLoading, setObrasLoading] = useState(false);
  const {
    open: isObrasOpen,
    onOpen: onObrasOpen,
    onClose: onObrasClose,
  } = useDisclosure();

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [consumption, catalog] = await Promise.all([
        adminAPI.getPlanConsumption(),
        adminAPI.getPlanCatalog(),
      ]);
      setRows(consumption.data || []);
      setFeatureKeys(catalog.data?.featureKeys || []);
    } catch (e) {
      toaster.create({ title: 'Error cargando consumos', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleChangePlan = async (userId, tier) => {
    try {
      await adminAPI.setUserPlan(userId, tier);
      toaster.create({ title: 'Plan actualizado', type: 'success' });
      load();
    } catch {
      toaster.create({ title: 'No se pudo actualizar el plan', type: 'error' });
    }
  };

  const openOverrides = (row) => {
    setSelected(row);
    setOverridesDraft({ ...(row.featureOverrides || {}) });
    setBudgetDraft(
      row.budgetOverrideEur != null ? String(row.budgetOverrideEur) : '',
    );
    onOpen();
  };

  const openObrasCost = async (row) => {
    if (!row?.bandaId) {
      toaster.create({
        title: 'Sin banda asociada',
        description: 'Este usuario no tiene bandaId para desglosar coste por obra.',
        type: 'warning',
      });
      return;
    }
    setObrasRow(row);
    setObrasData(null);
    onObrasOpen();
    setObrasLoading(true);
    try {
      const res = await adminAPI.getUsageObrasForBanda(row.bandaId);
      setObrasData(res.data);
    } catch {
      toaster.create({ title: 'No se pudo cargar el coste por obra', type: 'error' });
    } finally {
      setObrasLoading(false);
    }
  };

  const effectiveValue = (key) => {
    if (!selected) return false;
    if (key in overridesDraft && typeof overridesDraft[key] === 'boolean') {
      return overridesDraft[key];
    }
    return selected.effectiveFeatures?.[key] === true;
  };

  const isOverridden = (key) =>
    selected && key in overridesDraft && typeof overridesDraft[key] === 'boolean';

  const toggleOverride = (key) => {
    setOverridesDraft((prev) => {
      const next = { ...prev };
      if (key in next) {
        // ciclo: override true -> false -> quitar override
        if (next[key] === true) next[key] = false;
        else delete next[key];
      } else {
        next[key] = true;
      }
      return next;
    });
  };

  const handleSaveOverrides = async () => {
    if (!selected) return;
    setIsSaving(true);
    try {
      await adminAPI.setUserFeatureOverrides(selected.userId, overridesDraft);
      const parsedBudget = budgetDraft.trim() === '' ? null : Number(budgetDraft);
      await adminAPI.setUserBudgetOverride(selected.userId, parsedBudget);
      toaster.create({ title: 'Accesos actualizados', type: 'success' });
      onClose();
      load();
    } catch {
      toaster.create({ title: 'No se pudo guardar', type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Box p={10} textAlign="center">
        <Spinner size="lg" color="purple.500" />
      </Box>
    );
  }

  return (
    <Box p={6}>
      <VStack align="stretch" gap={4}>
        <Text fontSize="sm" color="gray.600">
          Consumo del ciclo de suscripción (IA) y ocupación de disco por banda.
          El análisis con IA se corta al 100% del presupuesto. Las subidas se
          cortan al llegar al tope de almacenamiento del plan. El ciclo de IA se
          reinicia el día de renovación de cada banda, no el día 1.
        </Text>
        <Box overflowX="auto">
          <Table.Root variant="simple" size="sm">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>Banda</Table.ColumnHeader>
                <Table.ColumnHeader>Plan</Table.ColumnHeader>
                <Table.ColumnHeader textAlign='end'>IA</Table.ColumnHeader>
                <Table.ColumnHeader textAlign='end'>Almacen.</Table.ColumnHeader>
                <Table.ColumnHeader textAlign='end'>Total</Table.ColumnHeader>
                <Table.ColumnHeader>Consumo</Table.ColumnHeader>
                <Table.ColumnHeader></Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {rows.map((r) => (
                <Table.Row key={r.userId} bg={r.budget?.exceeded ? 'red.50' : storageOverQuota(r.budget) ? 'orange.50' : undefined}>
                  <Table.Cell>
                    <Text fontWeight="600">{r.nombre}</Text>
                    <Text fontSize="xs" color="gray.500">{r.email}</Text>
                  </Table.Cell>
                  <Table.Cell>
                    <NativeSelect.Root>
                      <NativeSelect.Field
                        size="xs"
                        w="130px"
                        value={r.planTier}
                        onChange={(e) => handleChangePlan(r.userId, e.target.value)}>
                        {PLAN_TIERS.map((t) => (
                          <option key={t} value={t}>{PLAN_LABELS[t]}</option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </Table.Cell>
                  <Table.Cell fontSize="xs" textAlign='end'>{eur(r.budget?.iaCostEur)}</Table.Cell>
                  <Table.Cell fontSize="xs" textAlign='end'>{storageCell(r.budget)}</Table.Cell>
                  <Table.Cell fontWeight="600" textAlign='end'>{eur(r.budget?.totalEur)}</Table.Cell>
                  <Table.Cell minW="160px">
                    <Tooltip content={`${eur(r.budget?.totalEur)} de ${eur(r.budget?.budgetEur)}`}>
                      <Box>
                        <Progress.Root
                          value={Math.min(100, r.budget?.percent || 0)}
                          size="sm"
                          rounded="full"
                          colorPalette={
                            r.budget?.percent >= 100
                              ? 'red'
                              : r.budget?.percent >= 80
                                ? 'orange'
                                : 'green'
                          }>
                          <Progress.Track>
                            <Progress.Range />
                          </Progress.Track>
                        </Progress.Root>
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          {Math.round(r.budget?.percent || 0)}%
                          {r.budget?.exceeded && (
                            <Badge ml={2} colorPalette="red">Bloqueado</Badge>
                          )}
                        </Text>
                      </Box>
                    </Tooltip>
                  </Table.Cell>
                  <Table.Cell>
                    <HStack gap={2}>
                      <Button size="xs" variant="outline" colorPalette="purple" onClick={() => openOverrides(r)}>
                        Accesos
                      </Button>
                      <Tooltip
                        content={r.bandaId ? 'Desglose de coste IA por obra' : 'Sin banda asociada'}
                      >
                        <Button
                          size="xs"
                          variant="solid"
                          colorPalette="orange"
                          disabled={!r.bandaId}
                          onClick={() => openObrasCost(r)}
                          data-testid="admin-coste-obra"
                        >
                          Coste por obra
                        </Button>
                      </Tooltip>
                    </HStack>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      </VStack>

      <Dialog.Root open={isOpen} size='xl' scrollBehavior="inside" onOpenChange={e => {
        if (!e.open) {
          onClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                Accesos de {selected?.nombre}
              </Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                <VStack align="stretch" gap={4}>
                  <Box>
                    <Text fontSize="sm" fontWeight="600" mb={1}>
                      Tope de coste mensual (EUR)
                    </Text>
                    <Input
                      size="sm"
                      type="number"
                      placeholder={`Por plan (${eur(selected?.budget?.budgetEur)})`}
                      value={budgetDraft}
                      onChange={(e) => setBudgetDraft(e.target.value)}
                    />
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      Vacío = usar el presupuesto del plan.
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="600" mb={2}>
                      Override de funcionalidades
                    </Text>
                    <Text fontSize="xs" color="gray.500" mb={2}>
                      El interruptor fuerza el acceso (independiente del plan). Usa
                      &quot;Restablecer&quot; para volver al valor del plan.
                    </Text>
                    <SimpleGrid columns={{ base: 1, md: 2 }} gap={2}>
                      {(featureKeys.length ? featureKeys : Object.keys(FEATURE_LABELS)).map((key) => (
                        <HStack key={key} justify="space-between" borderWidth="1px" rounded="md" px={3} py={2}>
                          <Box>
                            <Text fontSize="sm">{FEATURE_LABELS[key] || key}</Text>
                            {isOverridden(key) && (
                              <Badge colorPalette="purple" fontSize="0.6rem">override</Badge>
                            )}
                          </Box>
                          <HStack>
                            <Switch
                              checked={effectiveValue(key)}
                              onValueChange={() => toggleOverride(key)}
                              colorPalette="purple"
                            />
                            {isOverridden(key) && (
                              <Button
                                size="xs"
                                variant="ghost"
                                onClick={() =>
                                  setOverridesDraft((prev) => {
                                    const next = { ...prev };
                                    delete next[key];
                                    return next;
                                  })
                                }
                              >
                                Restablecer
                              </Button>
                            )}
                          </HStack>
                        </HStack>
                      ))}
                    </SimpleGrid>
                  </Box>
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="ghost" mr={3} onClick={onClose}>Cancelar</Button>
                <Button colorPalette="purple" onClick={handleSaveOverrides} loading={isSaving}>
                  Guardar
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      <Dialog.Root open={isObrasOpen} size='xl' scrollBehavior="inside" onOpenChange={e => {
        if (!e.open) {
          onObrasClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content data-testid="admin-coste-obra-modal">
              <Dialog.Header>
                Coste por obra — {obrasRow?.nombre || 'Banda'}
              </Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                {obrasLoading ? (
                  <Box py={10} textAlign="center">
                    <Spinner color="orange.400" />
                  </Box>
                ) : (
                  <VStack align="stretch" gap={4}>
                    <Text fontSize="sm" color="gray.600">
                      Periodo:{' '}
                      {obrasData?.range?.from
                        ? new Date(obrasData.range.from).toLocaleDateString('es-ES')
                        : '—'}{' '}
                      →{' '}
                      {obrasData?.range?.to
                        ? new Date(obrasData.range.to).toLocaleDateString('es-ES')
                        : '—'}
                      . Total IA: {usd(obrasData?.totals?.costUsd)} (
                      {obrasData?.obras?.length || 0} obras con eventos)
                    </Text>
                    {!obrasData?.obras?.length ? (
                      <Text fontSize="sm" color="gray.500" data-testid="admin-coste-obra-empty">
                        No hay coste atribuible a obras en este periodo (eventos sin paginaId/obraId).
                      </Text>
                    ) : (
                      <Box overflowX="auto">
                        <Table.Root size="sm" variant="simple">
                          <Table.Header>
                            <Table.Row>
                              <Table.ColumnHeader>Obra</Table.ColumnHeader>
                              <Table.ColumnHeader textAlign='end'>Págs</Table.ColumnHeader>
                              <Table.ColumnHeader textAlign='end'>Tok in</Table.ColumnHeader>
                              <Table.ColumnHeader textAlign='end'>Tok out</Table.ColumnHeader>
                              <Table.ColumnHeader textAlign='end'>Coste</Table.ColumnHeader>
                            </Table.Row>
                          </Table.Header>
                          <Table.Body>
                            {obrasData.obras.map((o) => (
                              <Table.Row key={o.obraId}>
                                <Table.Cell>
                                  <Text fontWeight="600" fontSize="sm">{o.titulo}</Text>
                                  <Text fontSize="xs" color="gray.500">
                                    {o.calls} llamadas · {o.paginasConCoste} págs con coste
                                  </Text>
                                </Table.Cell>
                                <Table.Cell textAlign='end'>{o.totalPaginas}</Table.Cell>
                                <Table.Cell fontSize="xs" textAlign='end'>{o.tokensIn}</Table.Cell>
                                <Table.Cell fontSize="xs" textAlign='end'>{o.tokensOut}</Table.Cell>
                                <Table.Cell fontWeight="600" textAlign='end'>{usd(o.costUsd)}</Table.Cell>
                              </Table.Row>
                            ))}
                          </Table.Body>
                        </Table.Root>
                      </Box>
                    )}
                  </VStack>
                )}
              </Dialog.Body>
              <Dialog.Footer>
                <Button onClick={onObrasClose}>Cerrar</Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>
    </Box>
  );
};

export default AdminPlanesPanel;
